import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Button,
  Grid,
  Hidden,
  Box,
  Typography,
  Divider,
  Paper,
  Container,
  Stack,
  useTheme,
} from "@mui/material";
import {
  StyledPieChart,
  StyledBarChart,
  StatsCard,
} from "../../../components/Charts";
import { useNavigate } from "react-router-dom";
import {
  AddCircleOutlined as AddIcon,
  BarChart as ChartIcon,
  Public as WebIcon,
  ViewModule as GridIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";

import { useBreadcrumbs } from "../../../hooks/useBreadcrumbs";
import {
  FirebaseAuth,
  Firestore,
} from "../../../components/FirebaseAuth/firebase";
import Loader from "../../../components/Loader";
import AccountCard from "../../../components/AccountCard";
import { isKWALLAdmin } from "../../../utils/index";
import {
  LOCAL_STORAGE_CURRENT_ORG,
  LOCAL_STORAGE_CURRENT_USER,
  LOCAL_STORAGE_UPDATED_AT,
  getLocalStorage,
  shouldRefreshOrgData,
  setLocalStorage,
} from "../../../utils/localStorage";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import AIAssistant from "../../../components/AIAssistant";
import DiffbotProfile from "../../../components/Organizations/DiffbotProfile";
import { useSiteCount } from "../../../hooks/useSiteCount";
import AggregatedTrafficChart from "../../../components/AggregatedTrafficChart/AggregatedTrafficChart";

const Home = () => {
  const title = "My Collections";
  const history = useNavigate();

  const dataFetchedRef = useRef(false);
  const [currentOrganization, setCurrentOrganization] = useState(
      getLocalStorage(LOCAL_STORAGE_CURRENT_ORG)
  );

  const refreshOrgData = async (orgId, Firestore) => {
    try {
      const { doc, getDoc } = await import("firebase/firestore");
      const orgRef = doc(Firestore, "organizations", orgId);
      const orgSnapshot = await getDoc(orgRef);

      if (orgSnapshot.exists()) {
        const orgData = { ...orgSnapshot.data(), id: orgSnapshot.id };
        setLocalStorage(LOCAL_STORAGE_CURRENT_ORG, orgData);
        setLocalStorage(LOCAL_STORAGE_UPDATED_AT, new Date().toISOString());
        return orgData;
      }
      return null;
    } catch (error) {
      console.error("Error refreshing organization data:", error);
      return null;
    }
  };

  const orgTrafficData = useMemo(() => {
    const date = new Date();
    const month = date.getMonth() + 1;
    const dateKey = `${month < 10 ? "0" + month : month}-${date.getFullYear()}`;

    if (
        currentOrganization?.metrics?.[dateKey]?.["traffic"]?.["google"]?.[
            "organic"
            ]?.["etv"]
    ) {
      return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 0,
      }).format(
          Math.round(
              currentOrganization?.metrics?.[dateKey]?.["traffic"]?.["google"]?.[
                  "organic"
                  ]?.["etv"] ?? 0
          )
      );
    }
    return 0;
  }, [currentOrganization?.metrics]);

  const userData = getLocalStorage(LOCAL_STORAGE_CURRENT_USER);
  
  // Use the new breadcrumb service
  useBreadcrumbs();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [loadingSiteTypeSummary, setLoadingSiteTypeSummary] = useState(false);
  const [siteTypeSummary, setSiteTypeSummary] = useState([]);
  const [accountSiteCounts, setAccountSiteCounts] = useState({});
  const [orgProfile, setOrgProfile] = useState(null);
  const [orgProfileExpanded, setOrgProfileExpanded] = useState(false);

  // Use the new useSiteCount hook
  const { siteCount: totalSites, loading: loadingSiteCount } = useSiteCount(
      currentOrganization?.id
  );

  const COLORS = useMemo(
      () => ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28FF6"],
      []
  );

  // Check and refresh organization data if needed
  useEffect(() => {
    const checkAndRefreshOrgData = async () => {
      if (currentOrganization?.id && shouldRefreshOrgData()) {
        const refreshedData = await refreshOrgData(
            currentOrganization.id,
            Firestore
        );
        if (refreshedData) {
          setCurrentOrganization(refreshedData);
        }
      }
    };

    checkAndRefreshOrgData();
  }, [currentOrganization?.id]);

  // Fetch CMS summary for the organization
  const getSiteTypeSummary = useCallback(async () => {
    try {
      if (!currentOrganization?.id) {
        // Set default empty array to prevent continuous loading
        setSiteTypeSummary([]);
        return;
      }

      setLoadingSiteTypeSummary(true);

      // First try to get CMS summary
      let summaryRef = collection(
          Firestore,
          "organizations",
          currentOrganization.id,
          "cmsSummary"
      );
      let summaryData = await getDocs(summaryRef);

      // If no CMS summary exists, fall back to siteTypeSummary
      if (summaryData.empty) {
        summaryRef = collection(
            Firestore,
            "organizations",
            currentOrganization.id,
            "siteTypeSummary"
        );
        summaryData = await getDocs(summaryRef);
      }

      let summaryArray = [];
      summaryData.forEach((doc) => {
        summaryArray.push({ type: doc.id, count: doc.data().count });
      });

      // Even if empty, set the data to stop loading state
      setSiteTypeSummary(summaryArray);
    } catch (error) {
      console.error("Error fetching CMS summary:", error);
      // Set empty array on error to prevent continuous loading
      setSiteTypeSummary([]);
    } finally {
      setLoadingSiteTypeSummary(false);
    }
  }, [currentOrganization?.id]); // Only depend on id, not the whole object

  // Fetch organization profile data
  const getOrgProfile = useCallback(async () => {
    try {
      if (!currentOrganization?.id) {
        setOrgProfile(null);
        return;
      }

      const orgProfileDoc = await getDoc(doc(Firestore, `organizations/${currentOrganization.id}/orgProfile/current`));
      if (orgProfileDoc.exists()) {
        const profileData = orgProfileDoc.data();
        setOrgProfile(profileData);
      } else {
        setOrgProfile(null);
      }
    } catch (error) {
      console.error("Error fetching organization profile:", error);
      setOrgProfile(null);
    }
  }, [currentOrganization?.id]);

  // Fetch site counts for each account
  const getAccountSiteCounts = useCallback(async (accountIds) => {
    try {
      if (!accountIds || accountIds.length === 0) {
        setAccountSiteCounts({});
        return;
      }

      const counts = {};

      // Fetch site counts for each account in parallel
      await Promise.all(
          accountIds.map(async (accountId) => {
            try {
              const summaryRef = collection(Firestore, "accounts", accountId, "siteTypeSummary");
              const summaryData = await getDocs(summaryRef);

              // Calculate total count for this account
              const totalCount = summaryData.docs.reduce((sum, doc) => sum + doc.data().count, 0);
              counts[accountId] = totalCount;
            } catch (error) {
              console.error(`Error fetching site count for account ${accountId}:`, error);
              counts[accountId] = 0;
            }
          })
      );

      setAccountSiteCounts(counts);
    } catch (error) {
      console.error("Error fetching account site counts:", error);
      setAccountSiteCounts({});
    }
  }, []);

  // Fetch accounts the user has access to
  const getAccounts = useCallback(async () => {
    try {
      // Return early if we're already loading
      if (accounts.length > 0 && !loading) {
        return;
      }

      setLoading(true);
      let records = [];
      // Get current user ID
      const userId = FirebaseAuth.currentUser?.uid || "mock-user-id";

      // Check if user is kwallAdmin
      const isUserKwallAdmin = isKWALLAdmin(userData?.role);

      // If user is kwallAdmin and there's an active organization, show ALL collections of that org
      if (isUserKwallAdmin && currentOrganization?.id) {
        console.log('👑 kwallAdmin user detected - fetching ALL collections for organization:', currentOrganization.name);
        console.log('📋 Organization accounts array:', currentOrganization.accounts);

        if (Array.isArray(currentOrganization.accounts)) {
          // Batch get all accounts from the current organization
          const batch = [];
          for (let accountId of currentOrganization.accounts) {
            if (accountId) {
              batch.push(doc(Firestore, "accounts", accountId));
            }
          }

          // Fetch all accounts in the organization
          if (batch.length > 0) {
            const accountDocs = await Promise.all(
                batch.map((docRef) => getDoc(docRef))
            );

            accountDocs.forEach((snap) => {
              if (snap.exists()) {
                const accountData = snap.data();
                console.log('🔍 Checking account:', snap.id, accountData);
                // kwallAdmin sees all collections in org regardless of access rights
                if (!accountData.status || accountData.status !== "deleted") {
                  console.log('✅ Adding account to records:', accountData.name || 'Unnamed Account');
                  records.push({
                    id: snap.id,
                    name: accountData.name || 'Unnamed Account',
                    accountType: accountData.accountType,
                    subscriptionStatus: accountData.subscriptionStatus,
                  });
                } else {
                  console.log('❌ Account filtered out due to status:', accountData.status);
                }
              } else {
                console.log('❌ Account document does not exist:', snap.id);
              }
            });
          }
        }
      }
      // If kwallAdmin has no active organization, show collections they have regular access to
      else if (isUserKwallAdmin && !currentOrganization?.id) {
        console.log('👑 kwallAdmin user with no active organization - showing accessible collections');
        const accountRef = query(
            collection(Firestore, "accounts"),
            where("accountActivation", "==", true),
            where("access", "array-contains", userId)
        );
        const accountData = await getDocs(accountRef);

        accountData.forEach((account) => {
          if (!account.data().status || account.data().status !== "deleted") {
            records.push({
              id: account.id,
              name: account.data().name || 'Unnamed Account',
              accountType: account.data().accountType,
              subscriptionStatus: account.data().subscriptionStatus,
            });
          }
        });
      }
      // If there's a current organization and user is not kwallAdmin, filter by organization
      else if (currentOrganization?.id) {
        // For Admin or orgAdmin
        if (
            currentOrganization.admins?.includes(userId) &&
            Array.isArray(currentOrganization.accounts)
        ) {
          // Batch get accounts instead of fetching one by one
          const batch = [];
          for (let accountId of currentOrganization.accounts) {
            if (accountId) {
              batch.push(doc(Firestore, "accounts", accountId));
            }
          }

          // If there are accounts to fetch, get them in one batch
          if (batch.length > 0) {
            const accountDocs = await Promise.all(
                batch.map((docRef) => getDoc(docRef))
            );

            accountDocs.forEach((snap) => {
              if (snap.exists()) {
                const accountData = snap.data();
                records.push({
                  id: snap.id,
                  name: accountData.name || 'Unnamed Account',
                  accountType: accountData.accountType,
                  subscriptionStatus: accountData.subscriptionStatus,
                });
              }
            });
          }
        } else {
          // For regular users, just fetch accounts they have access to
          const accountRef = query(
              collection(Firestore, "accounts"),
              where("accountActivation", "==", true),
              where("access", "array-contains", userId)
          );
          const accountData = await getDocs(accountRef);

          if (accountData.empty) {
            setAccounts([]);
            setLoading(false);
            return;
          }

          accountData.forEach((account) => {
            if (
                (!currentOrganization ||
                    currentOrganization?.accounts?.includes(account.id)) &&
                (!account.data().status || account.data().status !== "deleted")
            ) {
              records.push({
                id: account.id,
                name: account.data().name || 'Unnamed Account',
                accountType: account.data().accountType,
                subscriptionStatus: account.data().subscriptionStatus,
              });
            }
          });
        }
      } else {
        // No current organization, fetch using the account collection directly
        const accountRef = query(
            collection(Firestore, "accounts"),
            where("accountActivation", "==", true),
            where("access", "array-contains", userId)
        );
        const accountData = await getDocs(accountRef);

        if (accountData.empty) {
          // Redirect if no accounts found
          history("/new-account");
          return;
        }

        accountData.forEach((account) => {
          records.push({
            id: account.id,
            name: account.data().name || 'Unnamed Account',
            accountType: account.data().accountType,
            subscriptionStatus: account.data().subscriptionStatus,
          });
        });
      }

      setAccounts(records);

      // Fetch site counts for all accounts
      if (records.length > 0) {
        const accountIds = records.map(account => account.id);
        await getAccountSiteCounts(accountIds);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  }, [accounts.length, loading, currentOrganization, history, getAccountSiteCounts, userData?.role]);


  // Separate effect for data fetching to break dependency cycle
  useEffect(() => {
    if (dataFetchedRef.current) return;
    dataFetchedRef.current = true;

    const fetchData = async () => {
      await getAccounts();
      await getSiteTypeSummary();
      await getOrgProfile();
    };

    fetchData();
  }, [getAccounts, getSiteTypeSummary, getOrgProfile]);

  const theme = useTheme();

  // Total sites now comes from the useSiteCount hook
  // const totalSites = siteTypeSummary.reduce((sum, item) => sum + item.count, 0);

  // Calculate data for the bar chart
  const barChartData = useMemo(() => {
    const date = new Date();
    const month = date.getMonth() + 1;
    const dateKey = `${month < 10 ? "0" + month : month}-${date.getFullYear()}`;

    if (!currentOrganization?.metrics?.[dateKey]?.topSites?.length) {
      return [];
    }

    return currentOrganization.metrics[dateKey].topSites
        .filter((site) => site.traffic > 0)
        .map((site, index) => ({
          name: site.siteName || "Unnamed Site",
          value: site.traffic,
          fill: COLORS[index % COLORS.length],
        }));
  }, [COLORS, currentOrganization?.metrics]);

  // const updateSiteMatrics=async ()=>{
  //    const updateSitesMetrics = httpsCallable(
  //         CloudFunctions,
  //         "dataReporting.updateSitesMetrics"
  //       );
  //       await updateSitesMetrics();
  // }

  return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        {loading ? (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="70vh"
            >
              <Loader text="Loading dashboard data..." />
            </Box>
        ) : (
            <Stack spacing={3}>
              {/* Page Title and Actions */}
              <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
              >
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  Dashboard
                </Typography>
                {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={updateSiteMatrics}
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 1,
                boxShadow: theme.shadows[2],
                backgroundColor: theme.palette.primary.main
              }}
            >
              Update Site Matrics
            </Button> */}
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => history("/new-account")}
                    sx={{
                      borderRadius: "8px",
                      px: 3,
                      py: 1,
                      boxShadow: theme.shadows[2],
                      backgroundColor: theme.palette.primary.main,
                    }}
                >
                  Create Collection
                </Button>
              </Box>

              {/* Company Profile Section */}
              {orgProfile?.diffbotProfile && (
                  <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        mb: 1
                      }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Company Profile
                      </Typography>
                      <Button
                          size="small"
                          endIcon={orgProfileExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={() => setOrgProfileExpanded(!orgProfileExpanded)}
                          sx={{ textTransform: 'none' }}
                      >
                        {orgProfileExpanded ? 'Show Less' : 'Show More'}
                      </Button>
                    </Box>

                    <DiffbotProfile
                        diffbotData={orgProfile.diffbotProfile}
                        compact={!orgProfileExpanded}
                    />
                  </Paper>
              )}

              {/* Stats Overview Cards */}
              <Grid container spacing={3} style={{ marginLeft: "-24px" }}>
                <Grid item xs={12} md={4}>
                  <StatsCard
                      value={accounts.length}
                      label="Total Collections"
                      icon={<GridIcon />}
                      color="success"
                      onClick={() => history(`/organization/${currentOrganization?.id}/all-collections`)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <StatsCard
                      value={totalSites}
                      label="Total Sites"
                      icon={<WebIcon />}
                      color="primary"
                      onClick={() => history(`/organization/${currentOrganization?.id}/all-sites`)}
                  />
                </Grid>

                {/* <Grid item xs={12} md={6}>
              <StatsCard
                value={siteTypeSummary.length}
                label="Site Types"
                icon={<ChartIcon />}
                color="secondary"
              />
            </Grid> */}

                <Grid item xs={12} md={4}>
                  <StatsCard
                      value={orgTrafficData}
                      label="Estimated Traffic for next 30 days"
                      subtitle="This is the estimated traffic for next 30 days, actual traffic may vary"
                      icon={<ChartIcon />}
                      color="warning"
                  />
                </Grid>
              </Grid>

              {/* AI Assistant Section */}
              {currentOrganization && (
                  <Grid container spacing={3} style={{ marginLeft: "-24px" }}>
                    <Grid item xs={12}>
                      <AIAssistant
                          contextType="organization"
                          contextData={currentOrganization}
                          title="Organization AI Assistant"
                          maxHeight={400}
                      />
                    </Grid>
                  </Grid>
              )}

              {/* Organization Overview Card for Admins */}
              {(isKWALLAdmin(userData?.role) ||
                  currentOrganization?.admins?.includes(
                      FirebaseAuth.currentUser?.uid || "mock-user-id"
                  )) && (
                  <Grid container spacing={3} style={{ marginLeft: "-24px" }}>
                    {/* Pie Chart Card */}
                    <Grid item xs={12} md={6}>
                      <StyledPieChart
                          data={siteTypeSummary}
                          title="CMS Distribution"
                          subtitle="Breakdown of sites by content management system"
                          colors={COLORS}
                          dataKey="count"
                          nameKey="type"
                          height={240}
                          isLoading={loadingSiteTypeSummary}
                          showEmptyState={siteTypeSummary.length === 0}
                      />
                    </Grid>

                    {/* Bar Chart Card */}
                    <Grid item xs={12} md={6}>
                      <StyledBarChart
                          data={barChartData}
                          title="Top Performing Sites"
                          subtitle="Monthly estimated traffic by site"
                          dataKey="value"
                          xAxisDataKey="name"
                          height={240}
                          isLoading={loadingSiteTypeSummary}
                          showEmptyState={barChartData.length === 0}
                      />
                    </Grid>

                    {/* Traffic Analytics Chart - Full Width Below */}
                    {currentOrganization && (
                      <Grid item xs={12}>
                        <AggregatedTrafficChart 
                          level="organization" 
                          levelId={currentOrganization.id} 
                          title="Organization Traffic Analytics"
                          height={240}
                        />
                      </Grid>
                    )}
                  </Grid>
              )}


            </Stack>
        )}
      </Container>
  );
};

export default Home;